
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface Team {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
}

const TeamsList = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { hasRole } = useUserRoles();
  const { user } = useSupabaseUser();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isGuard = hasRole('guard');
  
  useEffect(() => {
    const fetchTeams = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        console.log("Fetching teams for guard user:", user.id);
        
        let teamsData: Team[] = [];
        
        // Fetch teams that the guard is a member of
        const { data: memberTeams, error: memberTeamsError } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', user.id);
          
        if (memberTeamsError) throw memberTeamsError;
        
        if (memberTeams && memberTeams.length > 0) {
          const teamIds = memberTeams.map(mt => mt.team_id);
          console.log("Guard team IDs:", teamIds);
          
          const { data: teamsDetails, error: teamsError } = await supabase
            .from('teams')
            .select('*')
            .in('id', teamIds);
            
          if (teamsError) throw teamsError;
          teamsData = teamsDetails || [];
        }
        
        console.log("Teams fetched:", teamsData);
        setTeams(teamsData);
      } catch (error) {
        console.error("Error fetching teams:", error);
        toast({
          title: "Error fetching teams",
          description: "An error occurred while fetching your teams",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeams();
  }, [user, toast]);

  const handleTeamClick = (teamId: string) => {
    // Redirect to the scheduling page with the team parameter
    navigate(`/scheduling?team=${teamId}`);
  };

  if (isLoading) {
    return (
      <div className="container py-6">
        <h1 className="text-2xl font-bold mb-6">My Teams</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-40 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="container py-6">
        <h1 className="text-2xl font-bold mb-6">My Teams</h1>
        <Alert variant="default" className="bg-muted">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No Teams Assigned
            <p className="text-sm mt-2">
              You are not currently assigned to any teams. Please contact your administrator if you believe this is an error.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container py-6">
      <h1 className="text-2xl font-bold mb-6">My Teams</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teams.map((team) => {
          // Generate subtle variations based on team ID for visual diversity
          const teamVariation = team.id.charCodeAt(0) % 6;
          
          const getGradientVariation = () => {
            switch (teamVariation) {
              case 0:
                return "from-blue-50/80 to-blue-100/60"; // Soft blue
              case 1:
                return "from-purple-50/80 to-purple-100/60"; // Soft purple
              case 2:
                return "from-emerald-50/80 to-emerald-100/60"; // Soft emerald
              case 3:
                return "from-orange-50/80 to-orange-100/60"; // Soft orange
              case 4:
                return "from-pink-50/80 to-pink-100/60"; // Soft pink
              case 5:
                return "from-teal-50/80 to-teal-100/60"; // Soft teal
              default:
                return "from-blue-50/80 to-blue-100/60";
            }
          };

          const getBorderColor = () => {
            switch (teamVariation) {
              case 0:
                return "border-blue-200/40";
              case 1:
                return "border-purple-200/40";
              case 2:
                return "border-emerald-200/40";
              case 3:
                return "border-orange-200/40";
              case 4:
                return "border-pink-200/40";
              case 5:
                return "border-teal-200/40";
              default:
                return "border-blue-200/40";
            }
          };

          return (
            <Card 
              key={team.id} 
              className={cn(
                "relative overflow-hidden group transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg rounded-xl border cursor-pointer",
                `bg-gradient-to-br ${getGradientVariation()}`,
                getBorderColor()
              )}
              onClick={() => handleTeamClick(team.id)}
            >
              {/* Decorative background pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 right-0 w-8 h-8 bg-white rounded-full -translate-y-4 translate-x-4"></div>
                <div className="absolute bottom-0 left-0 w-6 h-6 bg-white rounded-full translate-y-3 -translate-x-3"></div>
              </div>
              
              {/* Subtle shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
              
              <CardContent className="relative p-0">
                <div 
                  className="p-4 h-40 flex flex-col justify-between"
                  style={{
                    backgroundImage: team.image_url 
                      ? `linear-gradient(rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.6)), url(${team.image_url})` 
                      : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="flex justify-between">
                    <div className="w-8 h-8 rounded-lg bg-white/40 flex items-center justify-center backdrop-blur-sm border border-white/60 overflow-hidden">
                      {team.image_url ? (
                        <img 
                          src={team.image_url} 
                          alt={`${team.name} logo`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                          {team.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-700 group-hover:text-slate-600 transition-colors">{team.name}</h3>
                    {team.description && (
                      <p className="text-sm mt-1 text-slate-600/80 line-clamp-2">{team.description}</p>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <Button variant="outline" className="w-full bg-white/25 border-white/40 text-slate-700 hover:bg-white hover:text-slate-900 backdrop-blur-sm transition-all duration-300 font-medium">
                    View Schedule
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TeamsList;
